import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { renderFormField } from '../helpers/form-field.js';
import { nsFormFieldStyles, nsSharedStyles } from '../ns-styles/index.js';
import type { FieldValues } from '../models/company-data.js';
import type { DataField } from '../views/data/definition.js';
import { formatCell } from '../views/data/records-table.js';
import { fromInputValue, toInputValue, type ReferenceOption } from '../views/data/form-values.js';
import '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import type WaCheckbox from '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';

// Keeps no copy of the values: each change fires values-change with the full set for the caller to pass back
@customElement('ns-data-record-form')
export class NSDataRecordForm extends LitElement {
  @property({ type: Array })
  fields: DataField[] = [];

  @property({ type: Object })
  values: FieldValues = {};

  @property({ type: Boolean })
  readonly = false;

  // Per reference field key, the records it may point at
  @property({ type: Object, attribute: false })
  referenceOptions: Record<string, ReferenceOption[]> = {};

  // Per field key, a problem to show under it
  @property({ type: Object, attribute: false })
  errors: Record<string, string> = {};

  private __set(field: DataField, raw: string | boolean) {
    const values = { ...this.values };
    const value = fromInputValue(field, raw, this.referenceOptions[field.key]);
    if (value === undefined) delete values[field.key];
    else values[field.key] = value;
    this.dispatchEvent(new CustomEvent('values-change', { detail: { values }, bubbles: true, composed: true }));
  }

  private __label(field: DataField) {
    return `${field.label}${field.required ? ' (required)' : ''}${field.isPrivate ? ' (private)' : ''}`;
  }

  private __renderControl(field: DataField) {
    const id = `field-${field.key}`;
    const value = toInputValue(field, this.values[field.key]);
    const invalid = this.errors[field.key] ? 'true' : 'false';
    const describedBy = this.errors[field.key] ? `${id}-error` : nothing;
    const onInput = (e: Event) => this.__set(field, (e.target as HTMLInputElement).value);

    switch (field.kind) {
      case 'number':
        return html`
          <input
            id=${id}
            type="number"
            step="any"
            .value=${value}
            aria-invalid=${invalid}
            aria-describedby=${describedBy}
            @input=${onInput}
          />
        `;
      case 'date':
        return html`
          <input
            id=${id}
            type="date"
            .value=${value}
            aria-invalid=${invalid}
            aria-describedby=${describedBy}
            @input=${onInput}
          />
        `;
      case 'choice':
        return html`
          <select id=${id} .value=${value} aria-invalid=${invalid} aria-describedby=${describedBy} @change=${onInput}>
            <option value="">Not set</option>
            ${field.choices.map(
              (choice) => html`
                <option value=${choice} ?selected=${choice === value}>${choice}</option>
              `,
            )}
          </select>
        `;
      case 'reference':
        return html`
          <select id=${id} .value=${value} aria-invalid=${invalid} aria-describedby=${describedBy} @change=${onInput}>
            <option value="">Not set</option>
            ${(this.referenceOptions[field.key] ?? []).map(
              (option) => html`
                <option value=${option.ref.recordId} ?selected=${option.ref.recordId === value}>${option.label}</option>
              `,
            )}
          </select>
        `;
      default:
        return html`
          <input
            id=${id}
            type="text"
            .value=${value}
            aria-invalid=${invalid}
            aria-describedby=${describedBy}
            @input=${onInput}
          />
        `;
    }
  }

  private __renderField(field: DataField) {
    if (field.kind === 'yesNo') {
      return html`
        <div class="field">
          <wa-checkbox
            .checked=${Boolean(this.values[field.key])}
            @change=${(e: Event) => this.__set(field, (e.target as WaCheckbox).checked)}
          >
            ${this.__label(field)}
          </wa-checkbox>
          ${field.help
            ? html`
                <p class="hint">${field.help}</p>
              `
            : nothing}
        </div>
      `;
    }
    return renderFormField({
      id: `field-${field.key}`,
      label: this.__label(field),
      control: this.__renderControl(field),
      hint: field.help || undefined,
      error: this.errors[field.key],
    });
  }

  private __renderReadonly() {
    const titles = new Map(
      Object.values(this.referenceOptions)
        .flat()
        .map((option) => [option.ref.recordId, option.label]),
    );
    return html`
      <dl class="readonly">
        ${this.fields.map(
          (field) => html`
            <div class="row">
              <dt>${field.label}${field.isPrivate ? ' (private)' : ''}</dt>
              <dd>${formatCell(field, this.values[field.key], titles) || '—'}</dd>
            </div>
          `,
        )}
      </dl>
    `;
  }

  render() {
    if (this.readonly) return this.__renderReadonly();
    return html`
      <div class="form-panel">${this.fields.map((field) => this.__renderField(field))}</div>
    `;
  }

  static styles = [
    nsSharedStyles,
    nsFormFieldStyles,
    css`
      :host {
        display: block;
      }

      .readonly {
        margin: 0;
        display: grid;
        gap: var(--wa-space-xs);
        color: var(--primary-card-text-color);
      }

      .readonly .row {
        display: grid;
        grid-template-columns: minmax(8em, 1fr) 3fr;
        gap: var(--wa-space-s);
      }

      .readonly dt {
        font-weight: 600;
      }

      .readonly dd {
        margin: 0;
        overflow-wrap: anywhere;
      }
    `,
  ];
}

declare global {
  interface HTMLElementTagNameMap {
    'ns-data-record-form': NSDataRecordForm;
  }
}
